/**
 * NovelPageRenderer - Canonical Page Builder Renderer for Novel Detail Pages
 * Location: /app/frontend/src/components/page-builder/novel/
 * 
 * Features:
 * - Fetches page definition from Page Builder API
 * - Pre-flight entity check: verifies novel exists before rendering blocks
 * - AbortController with 10s timeout prevents infinite loading
 * - Distinct Not Found vs Server Error UI
 * - Admin-only debug flag for legacy renderer (default OFF)
 * - Feature flag: USE_CANONICAL_NOVEL_DETAIL in App.js
 */

import React, { useState, useEffect, useRef } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Loader2, AlertTriangle, BookText, Construction, Home, ArrowLeft, FileX } from 'lucide-react';
import { Button } from '../../ui/button';
import { Badge } from '../../ui/badge';
import { useRouteParams, useLegacyRendererDebug } from '../../CanonicalRouteProvider';
import { apiClient } from '../../../config/api';

// Import novel section renderers
import {
  NovelHeroSection,
  ChapterListSection,
  NovelInfoPanelSection,
  NovelCommentsSection,
  RelatedNovelsSection,
  NovelCreatorSpotlightSection,
  NovelCTASection,
} from './NovelSectionRenderers';

import AdminDebugPill from '../AdminDebugPill';

const FETCH_TIMEOUT_MS = 10000;

// Section type to component mapping
const NOVEL_SECTION_RENDERERS = {
  section_novel_hero: NovelHeroSection,
  section_chapter_list: ChapterListSection,
  section_novel_info: NovelInfoPanelSection,
  section_novel_comments: NovelCommentsSection,
  section_related_novels: RelatedNovelsSection,
  section_novel_creator: NovelCreatorSpotlightSection,
  section_novel_cta: NovelCTASection,
  novel_hero: NovelHeroSection,
  chapter_list: ChapterListSection,
  novel_info_panel: NovelInfoPanelSection,
  novel_comments: NovelCommentsSection,
  related_novels: RelatedNovelsSection,
  novel_creator_spotlight: NovelCreatorSpotlightSection,
  novel_cta: NovelCTASection,
  // Backwards compat with series template IDs
  series_hero: NovelHeroSection,
  episode_list: ChapterListSection,
  series_info_panel: NovelInfoPanelSection,
  series_comments: NovelCommentsSection,
  related_series: RelatedNovelsSection,
  series_creator_spotlight: NovelCreatorSpotlightSection,
  series_cta: NovelCTASection,
};

/** Loading state */
const PageLoading = () => (
  <div className="min-h-screen bg-black flex items-center justify-center" data-testid="novel-page-loading">
    <div className="text-center">
      <Loader2 className="w-8 h-8 text-orange-500 animate-spin mx-auto mb-4" />
      <p className="text-zinc-400">Loading novel...</p>
    </div>
  </div>
);

/** Not Found state - for invalid/missing novels */
const PageNotFound = ({ novelId }) => (
  <div className="min-h-screen bg-black flex items-center justify-center p-6" data-testid="novel-not-found">
    <div className="max-w-md text-center">
      <div className="w-16 h-16 bg-zinc-800 rounded-full flex items-center justify-center mx-auto mb-6">
        <FileX className="w-8 h-8 text-zinc-500" />
      </div>
      <h1 className="text-2xl font-bold text-white mb-2">Novel Not Found</h1>
      <p className="text-zinc-400 mb-6">
        {novelId
          ? `No novel exists with ID "${novelId.slice(0, 12)}..."`
          : 'No novel ID was provided.'}
      </p>
      <div className="flex gap-3 justify-center">
        <Button
          variant="outline"
          onClick={() => window.history.back()}
          className="border-zinc-700 text-zinc-300"
          data-testid="novel-not-found-back-btn"
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          Go Back
        </Button>
        <Link to="/novels">
          <Button className="bg-orange-500 hover:bg-orange-600" data-testid="novel-not-found-browse-btn">
            <BookText className="w-4 h-4 mr-2" />
            Browse Novels
          </Button>
        </Link>
      </div>
    </div>
  </div>
);

/** Error state - for server / timeout errors */
const PageError = ({ message, onRetry }) => (
  <div className="min-h-screen bg-black flex items-center justify-center p-6" data-testid="novel-page-error">
    <div className="max-w-md text-center">
      <AlertTriangle className="w-16 h-16 text-red-500 mx-auto mb-4" />
      <h1 className="text-2xl font-bold text-white mb-2">Failed to Load Page</h1>
      <p className="text-zinc-400 mb-6">{message || 'Something went wrong while loading this page.'}</p>
      <div className="flex gap-3 justify-center">
        {onRetry && (
          <Button onClick={onRetry} className="bg-orange-500 hover:bg-orange-600" data-testid="novel-error-retry-btn">
            Try Again
          </Button>
        )}
        <Link to="/">
          <Button variant="outline" className="border-zinc-700 text-zinc-300" data-testid="novel-error-home-btn">
            <Home className="w-4 h-4 mr-2" />
            Home
          </Button>
        </Link>
      </div>
    </div>
  </div>
);

/** Safe default template when no Page Builder definition exists */
const SafeDefaultTemplate = ({ novelId, isAdmin }) => (
  <div className="min-h-screen bg-black">
    <NovelHeroSection novelId={novelId} block={{ props: {} }} />
    <NovelInfoPanelSection novelId={novelId} block={{ props: {} }} />
    <ChapterListSection novelId={novelId} block={{ props: {} }} />
    {isAdmin && (
      <div className="bg-yellow-500/10 border border-yellow-500/30 p-4 mx-6 my-4 rounded-lg">
        <div className="flex items-center gap-2 text-yellow-500">
          <Construction className="w-5 h-5" />
          <span className="font-medium">Admin Notice</span>
        </div>
        <p className="text-yellow-400/80 text-sm mt-2">
          This novel page is using the default template. Customize it in Page Builder.
        </p>
      </div>
    )}
    <NovelCommentsSection novelId={novelId} block={{ props: {} }} />
  </div>
);

/** NovelPageRenderer - Main component */
const NovelPageRenderer = () => {
  const { novelId } = useParams();
  const routeParams = useRouteParams();
  const { legacyEnabled, isAdmin: isAdminDebug } = useLegacyRendererDebug();

  const [pageData, setPageData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const [isAdmin, setIsAdmin] = useState(false);
  const abortRef = useRef(null);

  const effectiveNovelId = novelId || routeParams?.novelId;

  // Check if user is admin
  useEffect(() => {
    const storedUser = localStorage.getItem('user');
    if (storedUser) {
      try {
        const user = JSON.parse(storedUser);
        setIsAdmin(user?.role === 'admin' || user?.is_admin);
      } catch (e) {
        setIsAdmin(false);
      }
    }
  }, []);

  // Pre-flight entity check + fetch page definition
  useEffect(() => {
    if (abortRef.current) abortRef.current.abort();

    const controller = new AbortController();
    abortRef.current = controller;
    const timedOut = { current: false };
    const timeoutId = setTimeout(() => {
      timedOut.current = true;
      controller.abort();
    }, FETCH_TIMEOUT_MS);

    const fetchData = async () => {
      if (!effectiveNovelId) {
        setNotFound(true);
        setLoading(false);
        return;
      }

      setLoading(true);
      setError(null);
      setNotFound(false);

      try {
        console.log('[NovelPageRenderer] Canonical rendering for novel:', effectiveNovelId);

        // Pre-flight: verify the novel exists
        const entityCheck = await apiClient.get(`/novels/${effectiveNovelId}`, {
          signal: controller.signal,
        });
        if (!entityCheck.data || entityCheck.data.error) {
          setNotFound(true);
          setLoading(false);
          return;
        }

        // Fetch page definition from Page Builder API
        const response = await apiClient.get('/page-builder/render/novel_detail', {
          signal: controller.signal,
        });
        setPageData(response.data);
      } catch (err) {
        if (controller.signal.aborted) {
          if (timedOut.current) {
            setError('Request timed out. Please check your connection and try again.');
          }
          // Cleanup abort — do nothing, let the next mount handle it
          setLoading(false);
          return;
        }
        if (err.response?.status === 404) {
          if (err.config?.url?.includes(`/novels/${effectiveNovelId}`)) {
            setNotFound(true);
          } else {
            setPageData({ page_id: 'novel_detail', blocks: [] });
          }
        } else {
          console.error('[NovelPageRenderer] Error:', err);
          setError(err.message || 'Failed to load page');
        }
      } finally {
        clearTimeout(timeoutId);
        setLoading(false);
      }
    };

    fetchData();

    return () => {
      clearTimeout(timeoutId);
      controller.abort();
    };
  }, [effectiveNovelId]);

  // Admin debug: use legacy renderer (novel has no legacy, show info)
  if (legacyEnabled) {
    return (
      <>
        <div className="min-h-screen bg-black flex items-center justify-center" data-testid="novel-legacy-mode">
          <div className="text-center">
            <AlertTriangle className="w-16 h-16 text-yellow-500 mx-auto mb-4" />
            <h1 className="text-2xl font-bold text-white mb-2">Legacy Mode</h1>
            <p className="text-zinc-400 mb-4">No legacy novel renderer exists.</p>
            <p className="text-zinc-500 text-sm">This toggle is for debugging comparison only.</p>
          </div>
        </div>
        <AdminDebugPill />
      </>
    );
  }

  if (loading) return <PageLoading />;
  if (notFound) return <PageNotFound novelId={effectiveNovelId} />;
  if (error) return <PageError message={error} onRetry={() => window.location.reload()} />;

  // No page data or empty blocks - use safe default
  if (!pageData || !pageData.blocks || pageData.blocks.length === 0) {
    return (
      <>
        <SafeDefaultTemplate novelId={effectiveNovelId} isAdmin={isAdmin} />
        <AdminDebugPill />
      </>
    );
  }

  // Render page via Page Builder blocks
  const enabledBlocks = pageData.blocks
    .filter(block => block.enabled !== false)
    .sort((a, b) => (a.order || 0) - (b.order || 0));

  return (
    <div
      className="min-h-screen bg-black"
      data-testid="novel-page-renderer"
      data-novel-id={effectiveNovelId}
    >
      {/* Canonical rendering badge (admin only) */}
      {isAdmin && (
        <div className="bg-green-500/10 border-b border-green-500/30 px-4 py-2">
          <div className="max-w-7xl mx-auto flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Badge className="bg-green-500/20 text-green-400 text-xs">
                Canonical PageBuilder Renderer
              </Badge>
              <span className="text-green-400/60 text-xs">
                Page ID: {pageData.page_id} | Blocks: {enabledBlocks.length}
              </span>
            </div>
          </div>
        </div>
      )}

      {/* Render each block using novel section renderers */}
      {enabledBlocks.map((block) => {
        const type = block._templateId || block.type;
        const Renderer = NOVEL_SECTION_RENDERERS[type];

        if (!Renderer) {
          console.warn('[NovelPageRenderer] Unknown block type:', type);
          return (
            <div key={block.id} className="py-4 px-6 bg-zinc-900/50 border border-zinc-800 my-2 mx-4 rounded">
              <p className="text-zinc-500 text-sm">Unknown section type: {type}</p>
            </div>
          );
        }

        return (
          <Renderer
            key={block.id}
            block={block}
            novelId={effectiveNovelId}
            isPreview={false}
          />
        );
      })}

      {/* Admin debug pill (only for admins) */}
      <AdminDebugPill />
    </div>
  );
};

export default NovelPageRenderer;
